import { CalendarClock, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { PageHeader } from "@/client/components/page-header";
import { StatCard } from "@/client/components/stat-card";
import { Badge } from "@/client/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import { Skeleton } from "@/client/components/ui/skeleton";
import { useAuthStore } from "@/client/features/auth/auth-store";
import { GuideCards } from "@/client/features/docs/placements";
import {
  type MaintenanceWindow,
  maintenanceApi,
} from "@/client/features/maintenance/maintenance-api";
import { isUpcoming } from "@/client/features/maintenance/upcoming";
import { teamApi } from "@/client/features/team/team-api";
import { ApiError } from "@/client/lib/api";
import { formatDateTime, formatRelativeTime } from "@/client/lib/format";

interface DashboardData {
  windows: MaintenanceWindow[];
  teamCount: number;
}

export function CustomerDashboard() {
  const user = useAuthStore((s) => s.user);
  const orgName = useAuthStore((s) => s.impersonation?.tenantName ?? s.user?.tenantName ?? null);
  const firstName = user?.name?.trim().split(/\s+/)[0] ?? "there";

  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [maintenance, members] = await Promise.all([
          maintenanceApi.list(),
          teamApi.listMembers(),
        ]);
        if (cancelled) return;
        setData({
          windows: maintenance.windows,
          teamCount: members.users.length,
        });
        setError(false);
      } catch (err) {
        if (cancelled) return;
        setError(true);
        toast.error(err instanceof ApiError ? err.message : "Failed to load your dashboard.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const upcoming = (data?.windows ?? [])
    .filter((w) => isUpcoming(w))
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
  const loadFailed = error && data === null;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`Welcome back, ${firstName}`}
        description={orgName ? `${orgName} portal home.` : "Your portal home."}
      />

      <GuideCards slot="dashboard.guides" />

      {loadFailed ? (
        <Card>
          <CardContent className="text-muted-foreground py-6 text-center text-sm">
            Couldn't load your dashboard. Refresh to try again.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <StatCard
              to="/calendar"
              label="Upcoming maintenance"
              value={data === null ? null : upcoming.length}
              hint="Scheduled or in progress"
              icon={CalendarClock}
            />
            <StatCard
              to="/team"
              label="Team members"
              value={data === null ? null : data.teamCount}
              hint="People in your org"
              icon={Users}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <CalendarClock className="text-muted-foreground size-4" />
                Upcoming maintenance
              </CardTitle>
              <CardDescription>Scheduled work that may affect your services.</CardDescription>
            </CardHeader>
            <CardContent>
              {data === null ? (
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : upcoming.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  No maintenance scheduled. We'll post a banner here and on the{" "}
                  <Link to="/calendar" className="text-foreground underline underline-offset-4">
                    calendar
                  </Link>{" "}
                  when something is planned.
                </p>
              ) : (
                <ul className="flex flex-col divide-y">
                  {upcoming.slice(0, 4).map((w) => (
                    <li
                      key={w.id}
                      className="flex items-center justify-between gap-3 py-2 first:pt-0"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{w.title}</p>
                        <p className="text-muted-foreground text-xs">
                          {new Date(w.startsAt).getTime() > Date.now()
                            ? `${formatRelativeTime(w.startsAt)} · ${formatDateTime(w.startsAt)}`
                            : formatDateTime(w.startsAt)}
                        </p>
                      </div>
                      {w.kind === "emergency" ? (
                        <Badge variant="destructive">Emergency</Badge>
                      ) : (
                        <Badge variant="secondary">
                          {w.status === "in_progress" ? "In progress" : "Scheduled"}
                        </Badge>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
